import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { TeamWithDetails, TeamWithStats } from "../types/team.types";
import { useToast } from "../components/toast";
import { TeamsAPI } from "../APIs/teams.api";
import { useErrorHandler } from "../hooks/useErrorHandler";
import { DiffResult } from "../utils/object-diff.utils";
import { TeamMembersAPI } from "../APIs/team_members.api";
import { useAuth } from "./AuthContext";
import { UserRoles } from "../types/user.types";
import { useCompetitions } from "./CompetitionsContext";

export interface TeamType {
  team: TeamWithDetails | null;
  teams: TeamWithStats[] | null;
  isLoading: boolean;
  error: string | null;
  getTeam: () => Promise<void>;
  getTeams: (competitionId?: number) => Promise<void>;
  updateTeam: (changes: DiffResult) => Promise<void>;
  handleError: (
    message: string,
    options?: { persist?: boolean; toast?: boolean; toastDuration?: number }
  ) => void;
  newMemberCredentials: {
    firstName: string;
    lastName: string;
    username: string;
    password: string;
  } | null;
  setNewMemberCredentials: (
    credentials: {
      firstName: string;
      lastName: string;
      username: string;
      password: string;
    } | null
  ) => void;
  removeMember: (memberId: number) => Promise<void>;
}

const TeamContext = createContext<TeamType | undefined>(undefined);

export const TeamProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [team, setTeam] = useState<TeamWithDetails | null>(null);
  const [teams, setTeams] = useState<TeamWithStats[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { error, handleError } = useErrorHandler();
  const { addToast } = useToast();
  const { user } = useAuth();
  const [newMemberCredentials, setNewMemberCredentials] = useState<{
    firstName: string;
    lastName: string;
    username: string;
    password: string;
  } | null>(null);
  const { currentCompetitionId } = useCompetitions();

  const getTeam = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await TeamsAPI.getTeam();
      if (response.statusCode === 200) {
        const data = await response.data;
        setTeam(data);
        setTeams([]);
      } else {
        handleError(response.message);
      }
    } catch (error) {
      handleError("Error al obtener el equipo: " + error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [handleError]);

  const getTeams = useCallback(
    async (competitionId?: number) => {
      setIsLoading(true);
      try {
        const response = await TeamsAPI.getTeam(competitionId);
        if (response.statusCode === 200) {
          const data = await response.data;
          if (Array.isArray(data)) {
            setTeams(data);
          } else {
            setTeams([]);
          }
        } else {
          handleError(response.message);
        }
      } catch (error) {
        handleError("Error al obtener los equipos: " + error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [handleError]
  );

  const updateTeam = useCallback(
    async (changes: DiffResult) => {
      try {
        const response = await TeamsAPI.updateTeam(changes);
        if (response.statusCode === 200) {
          const data = response.data as TeamWithDetails;
          setTeam(data);
          addToast({
            message: "¡Equipo actualizado!",
            type: "success",
            location: "bottom-center",
          });
        } else {
          handleError(response.message);
          throw error;
        }
      } catch (error) {
        handleError("Error al actualizar el equipo: " + error);
      }
    },
    [handleError, addToast, error]
  );

  const removeMember = useCallback(
    async (memberId: number) => {
      try {
        const response = await TeamMembersAPI.removeTeamMember(memberId);
        if (response.statusCode === 200) {
          if (
            newMemberCredentials &&
            team?.members.some(
              (member) => member.username === newMemberCredentials.username
            )
          ) {
            setNewMemberCredentials(null);
          }
          await getTeam();
          addToast({
            message: "Miembro eliminado correctamente",
            type: "success",
            location: "bottom-center",
          });
        } else {
          handleError(response.message);
        }
      } catch (error) {
        handleError("Error al eliminar el miembro: " + error);
      }
    },
    [newMemberCredentials, team, getTeam, handleError, addToast]
  );

  useEffect(() => {
    if (
      user?.roleId === UserRoles.CLIENT_ADMIN ||
      user?.roleId === UserRoles.SYSTEM_ADMIN
    ) {
      if (!currentCompetitionId) {
        setIsLoading(true);
        return;
      }
      setIsLoading(true);
      getTeams(currentCompetitionId);
    }
  }, [currentCompetitionId, user?.roleId, getTeams]);

  return (
    <TeamContext.Provider
      value={{
        team,
        teams,
        isLoading,
        error,
        getTeam,
        getTeams,
        updateTeam,
        handleError,
        newMemberCredentials,
        setNewMemberCredentials,
        removeMember,
      }}
    >
      {children}
    </TeamContext.Provider>
  );
};

export const useTeam = () => {
  const context = useContext(TeamContext);
  if (!context) {
    throw new Error("useTeamContext must be used within a TeamProvider");
  }
  return context;
};
